import { useMemo, useState } from 'react';
import { Search, Plus, Package, ImageOff } from 'lucide-react';

/**
 * KeepsakesGrid — Phase 2 redesign of the admin Keepsake catalog.
 * Uses existing tokens from backoffice-tokens.css (white surface, slate borders, ops-pill).
 *
 * Filters/sort intentionally minimal and consistent with LeadsTable.
 */
const SORT_OPTIONS = [
  { v: 'display_order', l: 'Display order' },
  { v: 'name',          l: 'Name (A→Z)' },
  { v: 'recent',        l: 'Recently updated' },
];

export default function KeepsakesGrid({ keepsakes, onAdd, onEdit }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | hidden
  const [sortBy, setSortBy] = useState('display_order');

  const visible = useMemo(() => {
    let arr = keepsakes.slice();
    if (statusFilter === 'active') arr = arr.filter(k => k.is_active);
    if (statusFilter === 'hidden') arr = arr.filter(k => !k.is_active);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      arr = arr.filter(k =>
        (k.name || '').toLowerCase().includes(s) ||
        (k.description || '').toLowerCase().includes(s)
      );
    }
    const getKey = {
      display_order: k => k.display_order ?? 99,
      name:          k => (k.name || '').toLowerCase(),
      recent:        k => k.updated_at || k.created_at || '',
    }[sortBy] || (k => k.display_order ?? 99);
    arr.sort((a, b) => {
      const va = getKey(a); const vb = getKey(b);
      if (sortBy === 'recent') { if (va < vb) return 1; if (va > vb) return -1; return 0; }
      if (va < vb) return -1; if (va > vb) return 1; return 0;
    });
    return arr;
  }, [keepsakes, search, statusFilter, sortBy]);

  return (
    <div className="px-6 py-5" data-testid="keepsakes-grid">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-[14px] font-semibold text-slate-900">Keepsake catalog</h2>
          <p className="text-[12px] text-slate-500 mt-0.5">Items clients can select for their Heirloom Box.</p>
        </div>
        <button
          onClick={onAdd}
          data-testid="add-keepsake-button"
          className="h-8 flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 rounded-md text-[13px] font-medium shadow-sm transition-colors"
        >
          <Plus size={13} /> Add keepsake
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search keepsakes…"
            data-testid="search-keepsakes"
            className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md pl-8 pr-2.5 text-[13px] text-slate-900 outline-none placeholder:text-slate-400 transition-colors"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          data-testid="filter-keepsake-status"
          className="h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-700 outline-none transition-colors"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="hidden">Hidden</option>
        </select>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          data-testid="sort-keepsakes"
          className="h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-700 outline-none transition-colors"
        >
          {SORT_OPTIONS.map(o => <option key={o.v} value={o.v}>Sort: {o.l}</option>)}
        </select>
        <span className="text-[12px] text-slate-500 sm:ml-1" data-testid="keepsakes-count">{visible.length} item{visible.length === 1 ? '' : 's'}</span>
      </div>

      {/* Grid */}
      {visible.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm text-center py-16 px-6">
          <Package size={24} className="mx-auto text-slate-300 mb-3" />
          <p className="text-[14px] text-slate-700 font-medium mb-1">No keepsakes match the current filters</p>
          <p className="text-[12px] text-slate-500">Clear filters or add a new keepsake.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {visible.map(k => (
            <button
              key={k.id}
              onClick={() => onEdit(k)}
              data-testid={`keepsake-row-${k.id}`}
              className="text-left bg-white border border-slate-200 hover:border-slate-400 hover:shadow-sm rounded-lg overflow-hidden transition-all duration-150 group"
            >
              <div className="aspect-[4/5] w-full bg-slate-100 overflow-hidden relative">
                {k.photo_url ? (
                  <img
                    src={k.photo_url}
                    alt={k.name}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                    onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-slate-300">
                    <ImageOff size={28} />
                  </div>
                )}
                {/* Top-right badges overlay */}
                <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
                  {k.is_always_included && (
                    <span className="ops-pill bg-slate-900 text-white border-slate-900">Always included</span>
                  )}
                  {!k.is_active && (
                    <span className="ops-pill bg-red-50 text-red-700 border-red-100">Hidden</span>
                  )}
                </div>
              </div>
              <div className="px-3 py-2.5 border-t border-slate-100">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-[13px] font-medium text-slate-900 truncate">{k.name}</p>
                  <span className="text-[11px] text-slate-400 shrink-0">#{k.display_order ?? '—'}</span>
                </div>
                {k.description && <p className="text-[12px] text-slate-500 line-clamp-2 leading-snug">{k.description}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
